<template>
  <div class="phone-input--wrap relative">
    <div class="phone-input" :class="{ 'has-error': error }">
      <div
        class="cursor-pointer h-full pl-3 rtl:pl-0 rtl:pr-3 flex items-center justify-center gap-1.5 hover:bg-neutral-100"
        @click="toggleCountryDropdown"
      >
        <div class="flex flex-row items-center text-gray-700 gap-1">
          <div
            v-if="showFlagInSelection && activeCountry && activeCountry.emoji"
            class="mr-1 rtl:mr-0 rtl:ml-1"
          >
            {{ activeCountry.emoji }}
          </div>
          <div class="font-semibold">{{ activeCountryCode }}</div>
          <div>({{ activeDialCode }})</div>
        </div>
        <span
          class="material-symbols-outlined text-[20px] mt-[3px] pr-1 border-r rtl:pr-0 rtl:pl-1 rtl:border-r-0 rtl:border-l border-[#D1D5DB]"
        >
          keyboard_arrow_down
        </span>
      </div>
      <input
        :value="phoneNumber"
        type="tel"
        class="phone-input--field rtl:text-right rtl:pl-3 ltr:pr-3"
        :placeholder="placeholder"
        :readonly="readonly"
        :style="styles"
        @input="onChange"
        @focus="$emit('focus')"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="showDropdown"
      ref="dropdown"
      class="country-dropdown"
      :class="
        dropdownDirection === 'top' ? 'dropdown--top' : 'dropdown--bottom'
      "
    >
      <div v-if="enableSearchCountry" class="dropdown-search--wrap">
        <input
          ref="searchbar"
          v-model="searchCountry"
          type="text"
          placeholder="Search"
          class="dropdown-search"
        />
      </div>
      <div
        v-for="(country, index) in filteredCountriesBySearch"
        ref="dropdownItem"
        :key="index"
        class="country-dropdown--item"
        :class="{
          active: country.id === activeCountryCode,
          focus: index === selectedIndex,
        }"
        @click="onSelectCountry(country)"
      >
        <span class="text-base mr-1">{{ country.emoji }}</span>

        <span
          class="max-w-[7.5rem] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ country.name }}
        </span>
        <span class="ml-1 text-slate-300 dark:text-slate-300 text-xs">{{
          country.dial_code
        }}</span>
      </div>
      <div v-if="filteredCountriesBySearch.length === 0">
        <span
          class="flex items-center justify-center text-sm text-slate-500 dark:text-slate-300 mt-4"
        >
          No results found
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import {
  hasPressedArrowUpKey,
  hasPressedArrowDownKey,
  isEnter,
} from 'shared/helpers/KeyboardHelpers';

export default {
  mixins: [eventListenerMixins],
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
    showFlagInSelection: {
      type: Boolean,
      default: true,
    },
    dropdownDirection: {
      type: String,
      default: 'bottom',
      validator: value => ['top', 'bottom'].includes(value),
    },
    defaultCountry: {
      type: String,
      default: 'IL',
    },
    onlyCountries: {
      type: Array,
      default: () => ['IL', 'US', 'ES'],
    },
    enableSearchCountry: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'combine',
      validator: value => ['combine', 'separate'].includes(value),
    },
    disableCountrySelect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countries: countries.filter(country =>
        this.onlyCountries.includes(country.id)
      ),
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: '',
      activeDialCode: '',
      phoneNumber: this.value,
    };
  },
  computed: {
    filteredCountriesBySearch() {
      return this.countries.filter(country => {
        const { name, dial_code, id } = country;
        const search = this.searchCountry.toLowerCase();
        return (
          name.toLowerCase().includes(search) ||
          dial_code.toLowerCase().includes(search) ||
          id.toLowerCase().includes(search)
        );
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => country.id === this.activeCountryCode
        );
      }
      return '';
    },
  },
  watch: {
    value() {
      const number = parsePhoneNumber(this.value);
      if (number) {
        this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
        this.phoneNumber = number.nationalNumber;
      }
    },
  },
  mounted() {
    window.addEventListener('mouseup', this.onOutsideClick);
    this.setActiveCountry();
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onOutsideClick);
  },
  methods: {
    emitValue(dialCode, phoneNumber) {
      if (this.mode === 'combine') {
        this.$emit('input', `${dialCode}${phoneNumber}`);
      } else {
        this.$emit('input', phoneNumber, dialCode);
      }
    },

    onChange(e) {
      this.phoneNumber = e.target.value;
      this.emitValue(this.activeDialCode, e.target.value);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },

    setActiveCountry() {
      const number = this.value ? parsePhoneNumber(this.value) : null;
      if (number) {
        this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
        this.phoneNumber = number.nationalNumber;
        return;
      }
      const countryId =
        this.countries.find(c => this.value?.startsWith(c.dial_code))?.id ??
        this.defaultCountry;
      const country = this.countries.find(c => c.id === countryId);
      if (country) {
        this.activeCountryCode = country.id;
        this.activeDialCode = country.dial_code;
      }
    },
    onSelectCountry(country) {
      this.activeCountryCode = country.id;
      this.activeDialCode = country.dial_code;
      this.searchCountry = '';
      this.$emit('setCode', country.dial_code);
      if (this.mode === 'combine') {
        this.emitValue(country.dial_code, this.phoneNumber);
      }
      this.closeDropdown();
    },

    toggleCountryDropdown() {
      if (this.disableCountrySelect) return;
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown && this.enableSearchCountry) {
        this.$nextTick(() => {
          this.$refs.searchbar.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
    onOutsideClick(e) {
      if (
        this.showDropdown &&
        e.target !== this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },

    onKeyDownHandler(e) {
      if (!this.showDropdown) return;

      if (hasPressedArrowDownKey(e)) {
        e.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.filteredCountriesBySearch.length - 1
        );
        this.$refs.dropdown.scrollTop = this.selectedIndex * 28;
      } else if (hasPressedArrowUpKey(e)) {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.$refs.dropdown.scrollTop = this.selectedIndex * 28 - 56;
      } else if (isEnter(e)) {
        e.preventDefault();
        this.onSelectCountry(
          this.filteredCountriesBySearch[this.selectedIndex]
        );
      }
    },
  },
};
</script>
<style scoped lang="scss">
.phone-input--wrap {
  .phone-input {
    @apply h-[44px] flex items-center justify-start rounded-[8px] border border-solid border-slate-200 dark:border-slate-600 overflow-hidden;

    &.has-error {
      @apply border border-solid border-[#B91C1C] dark:border-[#B91C1C];
    }
  }

  .phone-input--field {
    @apply mb-0 border-0 h-full;
  }

  .country-dropdown {
    @apply z-10 absolute max-h-60 w-[12.5rem] rounded-[8px] shadow-md overflow-y-auto px-0 pt-0 bg-white dark:bg-slate-900 border border-gray-300;
    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-[#c1c1c1] rounded-full;
    }

    &::-webkit-scrollbar-thumb:hover {
      @apply bg-gray-400;
    }

    &.dropdown--bottom {
      @apply top-[48px];
    }

    &.dropdown--top {
      @apply bottom-[48px];
    }

    .dropdown-search--wrap {
      @apply top-0 sticky bg-white dark:bg-slate-900 p-1;

      .dropdown-search {
        @apply h-8 mb-0 text-sm border border-solid border-slate-200 dark:border-slate-600;
      }
    }

    .country-dropdown--item {
      @apply flex items-center py-2 px-3 cursor-pointer gap-1 hover:bg-neutral-100;

      &.active {
        @apply bg-slate-50 dark:bg-slate-700;
      }

      &.focus {
        @apply bg-slate-25 dark:bg-slate-800;
      }
    }
  }
}
</style>
